/**
  * @class meleActorTimeline
  * @description
  *
  * # Purpose
  * Implements `<mele-actor-timeline>` custom web element for showing timeline with markers
  */

import meleActor from 'meleActor'
import msysListener from 'msysListener'

const PROPERTIES = _.extend({}, {
  ets: { description: 'event timestamp - current playback time in miliseconds', type: 'integer' },
  indexes: { type: 'array', description: 'list of indexes to be retrieved for styling time cells', default: ['h264', 'markered', 'lightbared', 'sirened'] },
  before: { description: 'time window before the current event timestamp in seconds', type: 'integer', default: 1800 },
  after: { description: 'time window after the current event timestamp in seconds', type: 'integer', default: 1800 },
  buffer: { description: 'additional number of seconds to fetch after current end of window', type: 'integer', default: 60 },
  prefetch: { description: 'how many seconds before running out of buffered data', type: 'integer', default: 10 },
  minor: { description: 'minor tick interval in seconds', type: 'integer', default: 60 },
  major: { description: 'major tick interval in seconds', type: 'integer', default: 600 },
}, meleActor._properties)

export default class meleActorTimeline extends meleActor {
  init() {
    super.init()
    this.innerHTML = '<svg></svg>'
    this.start_sec = 0
    this.end_sec = 0
    this.type_segments = {}
    window.addEventListener('resize', () => {
      this.refresh()
    })

    // we need our own listener to receive live updates on indexes
    // even when player is not playing live stream
    this.listener = new msysListener(0)
    this.listener.publish('#ME', 'mmStart')
    this.listener.mmMessage = this.liveMessage.bind(this)
  } // init

  connected(params) {
    const source = this.closest('mele-player')?.source
    if(source) {
      const filter = [['KEEP', 'action=mmIndex']]
      this.listener.publish(source, 'mmPlay', { start: 0, filter })
    }
  } // disconnected

  play(ets) {
    if(!(ets>=0)) ets = Date.now() * 1_000 // current time in microseconds
    // complete refresh on seek
    this.type_segments = {}
    this.start_sec = 0
    this.end_sec = 0
    this.refresh()
    this.etsChanged(ets)
  } // play

  async etsChanged(ets) {
    const sec = Math.floor(ets ? ets / 1_000_000 : Date.now() / 1_000)
    if(this.sec === sec) return
    this.sec = sec
    const start = sec - this.before
    const end = sec + this.after
    const live_end = end > Date.now() / 1_000

    let refresh_needed = false
    if(start >= this.end_sec || end <= this.start_sec) {
      // completely new window after current
      this.type_segments = {}

      // fetch indexes for missing beginning
      await this.fetchIndexes(start, end)
      refresh_needed = true
    }else {
      // partially overlapping window

      // delete old data outside new window
      for(const type in this.type_segments) {
        const segments = this.type_segments[type]
        for(let i = segments.length - 1; i >= 0; i--) {
          const seg = segments[i]
          if(seg[1] < start || seg[0] > end) {
            segments.splice(i, 1)
          }else{
            if(seg[0] < start) seg[0] = start
            if(seg[1] > end) seg[1] = end
          }
        }
      }

      if(start < this.start_sec) {
        // fetch indexes for missing beginning
        await this.fetchIndexes(start, this.start_sec, 'before')
        refresh_needed = true
      }
      if(!live_end && end >= this.end_sec - this.prefetch) {
        // fetch indexes for missing end
        await this.fetchIndexes(this.end_sec, end + this.buffer, 'after')
        refresh_needed = true
      }
    }

    if(refresh_needed) {
      this.refresh()
    }
  } // etsChanged

  liveMessage(sender, params) {
    if(params.action === 'mmIndex') {
      const { index, event_dt, value } = params?.params || {}
      if(!['h264', 'markered', 'lightbared', 'sirened'].includes(index) || !value) return
      const sec = Math.floor(event_dt / 1_000_000)
      if(!this.type_segments[index]) this.type_segments[index] = []
      const segments = this.type_segments[index]
      for(const seg of segments) {
        if(seg[0] <= sec && seg[1] >= sec) {
          // already present
          return
        }
        if(seg[0] === sec + 1) {
          // extend segment at begin
          seg[0] = sec
          this.refresh()
          return
        }
        if(seg[1] === sec - 1) {
          // extend segment at end
          seg[1] = sec
          this.refresh()
          return
        }
      }
      // create new segment
      segments.push([sec, sec])
      // keep segments sorted
      segments.sort((a, b) => a[0] - b[0])
      this.refresh()
    }
  } // mmIndex

  async fetchIndexes(start_sec, end_sec, direction) {
    const current_sec = this.sec
    const start = Math.floor(start_sec / 8) * 8 // align to 8 seconds
    const end = Math.ceil(end_sec / 8) * 8 // align to 8 seconds

    let refresh_needed = false
    for(const index of this.indexes) {
      try{
        const p = {
          index,
          start,
          end,
          mode: 'bits',
          level: 'second',
        }
        const player = this.closest('mele-player')
        const reply = await player.listener.request(player.source, 'mmGetIndexes', p, { resolve: 'mmIndexes', timeout: 2000 })
        const { start: start_sec, bits } = reply.params
        if(current_sec !== this.sec) return // time changed during fetching
        const segments = this.extractBitSegments(bits, start_sec)
        if(segments.length === 0) continue
        refresh_needed = true
        if(!this.type_segments[index]) this.type_segments[index] = []
        const existing = this.type_segments[index]

        // merge segments (they are both sorted by start time)
        if(direction === 'before') {
          const first_existing = existing[0]
          const last_new = segments[segments.length - 1]
          if(first_existing && last_new && last_new[1] >= first_existing[0]) {
            first_existing[0] = last_new[0]
            segments.pop()
          }
          this.type_segments[index] = segments.concat(existing)
        }else if(direction === 'after') {
          const last_existing = existing[existing.length - 1]
          const first_new = segments[0]
          if(last_existing && first_new && last_existing[1] >= first_new[0]) {
            last_existing[1] = first_new[1]
            segments.shift()
          }
          this.type_segments[index] = existing.concat(segments)
        }else {
          // full replace
          this.type_segments[index] = segments
        }
      }catch(e) {
        if(current_sec !== this.sec) return // time changed during fetching
      }
    }
    if(current_sec !== this.sec) return // time changed during fetching

    if(direction === 'before') {
      this.start_sec = start_sec
    }else if(direction === 'after') {
      this.end_sec = end_sec
    }else {
      this.start_sec = start_sec
      this.end_sec = end_sec
    }

    return refresh_needed
  } // fetchIndexes

  extractBitSegments(bytes, start_sec) {
    const out = []
    let currentStart = -1
    let bitPos = 0

    for(let i = 0; i < bytes.length; i++) {
      const b = bytes[i]

      if(b === 0) {
        if(currentStart !== -1) {
          out.push([start_sec + currentStart, start_sec + bitPos - 1])
          currentStart = -1
        }
        bitPos += 8
        continue
      }

      for(let bit = 0; bit < 8; bit++) {
        const v = (b >> bit) & 1
        if(v) {
          if(currentStart === -1) currentStart = bitPos
        } else {
          if(currentStart !== -1) {
            out.push([start_sec + currentStart, start_sec + bitPos - 1])
            currentStart = -1
          }
        }
        bitPos++
      }
    }

    if(currentStart !== -1) {
      out.push([start_sec + currentStart, start_sec + bitPos - 1])
    }

    return out
  } // extractBitSegments

  refresh() {
    const SVG_NS = 'http://www.w3.org/2000/svg'
    const svg = this.querySelector('svg')

    const current_sec = this.sec
    const start_sec = this.sec - this.before
    const end_sec = this.sec + this.after

    const me = this

    renderTimelineSvg(svg, this.type_segments, start_sec, end_sec)

    function renderTimelineSvg(svg, segments, start_sec, end_sec) {
      const width = svg.clientWidth
      const height = svg.clientHeight

      svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
      svg.setAttribute('preserveAspectRatio', 'none')

      // vyčisti SVG
      svg.innerHTML = ''
      // while(svg.firstChild) svg.removeChild(svg.firstChild)

      const duration = end_sec - start_sec
      if(duration <= 0) return

      const timeToX = sec => {
        if(sec < start_sec) sec = start_sec
        if(sec > end_sec) sec = end_sec
        const ratio = (sec - start_sec) / duration
        return ratio * width
      }

      const xToTime = x => {
        const ratio = x / width
        return start_sec + ratio * duration
      }

      // handle clicks
      svg.onclick = e => {
        const pt = svg.createSVGPoint()
        pt.x = e.clientX
        pt.y = e.clientY
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
        const clicked_sec = Math.floor(xToTime(svgP.x))
        const player = me.closest('mele-player')
        player.play(clicked_sec * 1_000_000)
      }

      // rozloženie "lanes"
      const laneHeight = 5
      const laneGap = 3
      const topPadding = 4

      const laneY = {
        h264: topPadding,
        markered: topPadding + (laneHeight + laneGap),
        lightbared: topPadding + 2 * (laneHeight + laneGap),
        sirened: topPadding + 3 * (laneHeight + laneGap),
      }

      // pozadie
      const bg = document.createElementNS(SVG_NS, 'rect')
      bg.setAttribute('class', 'background')
      bg.setAttribute('x', 0)
      bg.setAttribute('y', 0)
      bg.setAttribute('width', width)
      bg.setAttribute('height', height)
      svg.appendChild(bg)

      const drawSegmentRow = (items, y, css_class) => {
        if(!items) return
        for(const seg of items) {
          let [s, e] = seg
          if(e <= start_sec || s >= end_sec) continue
          if(s < start_sec) s = start_sec
          if(e > end_sec) e = end_sec
          if(e <= s) continue

          const x1 = timeToX(s)
          const x2 = timeToX(e)

          if(!x1 || !x2 || x2 <= x1) continue
          const r = document.createElementNS(SVG_NS, 'rect')
          r.setAttribute('class', css_class)
          r.setAttribute('x', x1)
          r.setAttribute('y', y)
          r.setAttribute('width', Math.max(1, x2 - x1))
          r.setAttribute('height', laneHeight)
          svg.appendChild(r)
        }
      }

      // časová os – ticks
      drawTimeTicks(svg, start_sec, end_sec, width, height, me.minor, me.major)

      // vykresli jednotlivé druhy markerov
      for(const index in segments) {
        drawSegmentRow(segments[index], laneY[index], index)
      }

      // aktuálny čas
      drawCurrentTime(svg, current_sec, start_sec, end_sec, width, height)
    }

    function drawCurrentTime(svg, current_sec, start_sec, end_sec, width, height) {
      if(current_sec < start_sec || current_sec > end_sec) return

      const x = ((current_sec - start_sec) / (end_sec - start_sec)) * width

      if(!x || isNaN(x)) return
      const marker = document.createElementNS(SVG_NS, 'line')
      marker.setAttribute('class', 'now')
      marker.setAttribute('x1', x)
      marker.setAttribute('x2', x)
      marker.setAttribute('y1', 0)
      marker.setAttribute('y2', height)

      svg.appendChild(marker)
    }

    function drawTimeTicks(svg, start_sec, end_sec, width, height, minor, major) {
      let t = Math.ceil(start_sec / minor) * minor

      const now_sec = Math.floor(Date.now() / 1_000)
      while(t <= end_sec) {
        if(t > now_sec) break
        const x = ((t - start_sec) / (end_sec - start_sec)) * width
        const isTen = (t % major) === 0

        const line = document.createElementNS(SVG_NS, 'line')
        line.setAttribute('x1', x)
        line.setAttribute('x2', x)
        line.setAttribute('stroke', '#aaa')

        if(isTen) {
          // hrubší 10-min tick
          line.setAttribute('class', 'tick2')
          line.setAttribute('y1', 4)
          line.setAttribute('y2', height - 26)

          const text = document.createElementNS(SVG_NS, 'text')
          text.setAttribute('class', 'label2')
          text.setAttribute('x', x)
          text.setAttribute('y', height)
          text.textContent = formatTimeLabel(t)
          svg.appendChild(text)
        } else {
          // obyčajný minútový tick
          line.setAttribute('class', 'tick1')
          line.setAttribute('y1', 4)
          line.setAttribute('y2', height - 4)
        }

        svg.appendChild(line)
        t += minor
      }
    }

    function formatTimeLabel(sec) {
      // ak sú to unix sekundy, toto dá lokálny čas HH:MM
      const d = new Date(sec * 1000)
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return `${hh}:${mm}`
    }
  } // refresh
} // class meleActorTimeline

meleActorTimeline._properties = PROPERTIES
meleActorTimeline.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-actor-timeline', meleActorTimeline)
